import { useCallback, useEffect, useRef, useState } from 'react';
import type { ScenarioInput, ScenarioResult } from '@exigo/shared';
import { useEvaluateMutation, useScenarioQuery, useUpdateScenarioMutation } from '../../api/queries';
import type { ScenarioRecord } from '../../lib/scenarioStore';

const DEBOUNCE_MS = 350;

/** The fields `updateMeta` may change — everything editable outside the calculator grid. */
type MetaPatch = Partial<
  Pick<
    ScenarioRecord,
    'name' | 'customerName' | 'opportunityRef' | 'status' | 'promoInput' | 'promoCatalogue'
  >
>;

/**
 * Owns one scenario's editing session: loads the record, holds the working
 * `ScenarioInput`, and keeps a debounced `/evaluate` call and the server-backed
 * record in sync with every edit.
 *
 * Every number the results panel shows comes from the server. This hook never
 * computes eligibility or a payout itself — it only decides *when* to ask.
 *
 * **`record` is optimistic.** CalculatorPage binds controlled inputs (the
 * scenario name, the customer name) straight to it, so it has to reflect a
 * keystroke in the same render that produced it. It cannot be a mirror of
 * server state: when it was, every character re-rendered the field with the
 * value the server had confirmed one round trip ago, which discarded the
 * selection and put the caret at the end — so select-all-and-retype appended
 * instead of replacing, and backspace looked like it did nothing.
 */
export function useScenarioForm(id: string): {
  /** `undefined` while loading, `null` if the id does not exist. */
  record: ScenarioRecord | null | undefined;
  input: ScenarioInput | null;
  setInput: (updater: (prev: ScenarioInput) => ScenarioInput) => void;
  result: ScenarioResult | null;
  isEvaluating: boolean;
  evaluateError: Error | null;
  updateMeta: (patch: MetaPatch) => void;
} {
  const scenarioQuery = useScenarioQuery(id);
  const updateMutation = useUpdateScenarioMutation();
  const evaluateMutation = useEvaluateMutation();

  const [record, setRecord] = useState<ScenarioRecord | null | undefined>(undefined);
  const [input, setInputState] = useState<ScenarioInput | null>(null);
  const [result, setResult] = useState<ScenarioResult | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Guards against re-seeding `input`/re-running the first evaluate every time
  // the query refetches this same id (e.g. after updateMeta's invalidation) —
  // only the very first arrival of a given id's data should do that.
  const seededForId = useRef<string | null>(null);

  // The debounced meta write. Keystrokes accumulate into one patch rather than
  // firing a PATCH per character — which also keeps the admin activity feed
  // from recording one `scenario_updated` event per letter typed.
  const metaTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingMeta = useRef<MetaPatch>({});
  const metaWritesInFlight = useRef(0);

  const runEvaluate = useCallback(
    (next: ScenarioInput, persist: boolean) => {
      evaluateMutation.mutate(
        { input: next },
        {
          onSuccess: (res) => {
            setResult(res);
            if (persist) {
              // Disjoint from the meta patch above on purpose: this writes only
              // `input`/`summary`, that one writes only the metadata fields, and
              // Drizzle's `.set()` skips keys it was not given — so the two can
              // be in flight together without either clobbering the other's
              // columns.
              updateMutation.mutate({
                id,
                patch: {
                  input: next,
                  summary: {
                    incrementalAcrUsd: res.incrementalAcrUsd,
                    claimableTotalUsd: res.claimableTotalUsd,
                    eligibleCount: res.eligibleCount,
                    bandLabel: null,
                  },
                },
              });
            }
          },
        },
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- evaluateMutation/updateMutation identity changes every render; keying on id is what matters here
    [id],
  );

  /** Sends whatever has accumulated, now. Safe to call with nothing pending. */
  const flushMeta = useCallback(
    () => {
      if (metaTimer.current) {
        clearTimeout(metaTimer.current);
        metaTimer.current = null;
      }
      const patch = pendingMeta.current;
      if (Object.keys(patch).length === 0) return;
      pendingMeta.current = {};
      metaWritesInFlight.current += 1;
      updateMutation.mutate(
        { id, patch },
        {
          onSettled: () => {
            metaWritesInFlight.current = Math.max(0, metaWritesInFlight.current - 1);
          },
        },
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- updateMutation identity changes every render; keying on id is what matters here
    [id],
  );

  /** True while the user has an edit this hook has not seen confirmed yet. */
  const hasUnsavedMeta = useCallback(
    () =>
      metaTimer.current !== null ||
      metaWritesInFlight.current > 0 ||
      Object.keys(pendingMeta.current).length > 0,
    [],
  );

  useEffect(() => {
    if (scenarioQuery.isLoading) return;
    const found = scenarioQuery.data ?? null;

    // First arrival of this id: seed everything and run the initial evaluate.
    if (found && seededForId.current !== id) {
      seededForId.current = id;
      setRecord(found);
      setInputState(found.input);
      runEvaluate(found.input, false);
      return;
    }

    // Every later refetch — most often the one our own write invalidated.
    // Adopting it while an edit is outstanding would push a stale value back
    // into a controlled input and fight the person typing into it.
    if (hasUnsavedMeta()) return;
    setRecord(found);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runEvaluate is re-created per id, which is the dependency that actually matters here
  }, [id, scenarioQuery.isLoading, scenarioQuery.data, hasUnsavedMeta]);

  const setInput = useCallback(
    (updater: (prev: ScenarioInput) => ScenarioInput) => {
      setInputState((prev) => {
        if (!prev) return prev;
        const next = updater(prev);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          runEvaluate(next, true);
        }, DEBOUNCE_MS);
        return next;
      });
    },
    [runEvaluate],
  );

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  const updateMeta = useCallback(
    (patch: MetaPatch) => {
      // Optimistic, and first — this is what makes the field usable at all.
      setRecord((prev) => (prev ? { ...prev, ...patch } : prev));
      pendingMeta.current = { ...pendingMeta.current, ...patch };
      if (metaTimer.current) clearTimeout(metaTimer.current);
      metaTimer.current = setTimeout(flushMeta, DEBOUNCE_MS);
    },
    [flushMeta],
  );

  // Leaving the page (or switching scenario) sends the pending edit rather than
  // dropping it — otherwise renaming a scenario and immediately clicking back
  // loses the new name, which looks exactly like the app ignoring the edit.
  useEffect(() => () => { flushMeta(); }, [flushMeta]);

  return {
    record,
    input,
    setInput,
    result,
    isEvaluating: evaluateMutation.isPending,
    evaluateError: evaluateMutation.error,
    updateMeta,
  };
}
